import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Grid, Stack, Typography } from '@mui/material';
import useImages from 'api/useImages';
import useReceiptsTheme from 'api/useReceiptsTheme';
import { EAssetType } from 'constants/enums';
import { useFormikContext } from 'formik';
import { UploadsTooltips } from 'hooks/useUpload';
import AcCard from 'components/AcCard/AcCard';
import AcInput from 'components/AcInput/AcInput';
import AcSelect from 'components/AcSelect/AcSelect';
import { ThemeLoader } from '../ThemeLoader';
import ReceiptPreview from './ReceiptPreview';
import { defaultReceiptTheme } from './defaults';
import { ReceiptThemeFormValues } from './types';
import 'style/forms.scss';
import { DefaultAssetsURLs } from 'constants/constants';

const ReceiptTheme = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { values, handleBlur, handleChange, setFieldValue, errors, touched } =
    useFormikContext<ReceiptThemeFormValues>();
  const { publisherId } = useParams();

  const defaultValues = useMemo(() => {
    if (!values) {
      return defaultReceiptTheme;
    }
    return {
      ...defaultReceiptTheme,
      ...values
    };
  }, [values]);

  const { getReceiptTheme } = useReceiptsTheme(publisherId);

  useEffect(() => {
    setFieldValue(
      'headerImage',
      getReceiptTheme.data?.receiptTheme?.headerImage
    );
  }, [getReceiptTheme.data?.receiptTheme?.headerImage, setFieldValue]);

  const pictures = useImages(publisherId).getImages;

  useEffect(
    () => setIsLoading(pictures.isLoading || !pictures.data),
    [pictures?.isLoading, pictures?.data]
  );

  if (isLoading) {
    return <ThemeLoader />;
  }

  return (
    <Grid container className="formContent">
      <Grid item xs={5}>
        <Typography
          variant="h6"
          sx={{
            marginBottom: '12px',
            fontFamily: 'Inter, Montserrat, serif',
            fontWeight: '600'
          }}
        >
          Receipt
        </Typography>
        {!pictures.isLoading && (
          <Stack gap={3}>
            <AcCard stackContainer={false}>
              <Grid container rowSpacing={2} columnSpacing={1.5}>
                <Grid item xs={12}>
                  <AcInput
                    header="Sender Name"
                    name="senderName"
                    value={defaultValues.senderName}
                    tooltip="The sender name shown in your customer's inbox. If left blank, the default account name will be used"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.senderName && Boolean(errors.senderName)}
                  />
                </Grid>
              </Grid>
            </AcCard>
            <AcCard stackContainer={false} title="Receipt logo">
              <Grid container rowSpacing={2} columnSpacing={1.5}>
                <Grid item xs={12}>
                  <AcSelect
                    header="Receipt Logo"
                    name="logo"
                    value={defaultValues.logo}
                    dataTestBoxId="receiptLogo"
                    uploadBtnTestId="uploadReceiptLogo"
                    dataTestListId="receiptLogoList"
                    optionDataTestIdPrefix="receiptLogoOption"
                    uploadConfig={{
                      onUploadSuccess: async (url: string) => {
                        await pictures.refetch();
                        setFieldValue('logo', url);
                      },
                      uploadType: EAssetType.LOGO,
                      uploadMessage: UploadsTooltips[EAssetType.LOGO]
                    }}
                    items={[
                      {
                        content: 'Default Appcharge logo',
                        key: DefaultAssetsURLs.defaultLogo,
                        value: DefaultAssetsURLs.defaultLogo,
                        renderFunction: () => {
                          return (
                            <Stack direction="row" alignItems="center" gap={1}>
                              <img
                                src={DefaultAssetsURLs.defaultLogo}
                                alt="Default Appcharge logo"
                                style={{ maxWidth: 26 }}
                              />
                              <Typography>Default Appcharge logo</Typography>
                            </Stack>
                          );
                        }
                      },
                      ...(pictures.data || [])
                        .filter((p: any) => p.type === EAssetType.LOGO)
                        .map((picture: any) => {
                          return {
                            content: picture.name,
                            key: picture.url,
                            value: picture.url,
                            renderFunction: () => {
                              return (
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  gap={1}
                                >
                                  <img
                                    src={picture.url}
                                    alt={picture.name}
                                    style={{ width: 26, maxHeight: 30 }}
                                  />
                                  <Typography>{picture.name}</Typography>
                                </Stack>
                              );
                            }
                          };
                        })
                    ]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.logo && Boolean(errors.logo)}
                  />
                </Grid>
              </Grid>
            </AcCard>
            <AcCard stackContainer={false} title="Receipt Header Image">
              <Grid container rowSpacing={2} columnSpacing={1.5}>
                <Grid item xs={12}>
                  <AcSelect
                    header="Receipt Header"
                    name="headerImage"
                    value={defaultValues.headerImage}
                    dataTestBoxId="receiptHeader"
                    uploadBtnTestId="uploadReceiptHeader"
                    dataTestListId="receiptHeaderList"
                    optionDataTestIdPrefix="receiptHeaderOption"
                    uploadConfig={{
                      onUploadSuccess: async (url: string) => {
                        await pictures.refetch();
                        setFieldValue('headerImage', url);
                      },
                      uploadType: EAssetType.RECEIPT_HEADER,
                      uploadMessage: UploadsTooltips[EAssetType.RECEIPT_HEADER]
                    }}
                    items={[
                      ...(pictures.data || [])
                        .filter(
                          (p: any) => p.type === EAssetType.RECEIPT_HEADER
                        )
                        .map((picture: any) => {
                          return {
                            content: picture.name,
                            key: picture.url,
                            value: picture.url,
                            renderFunction: () => {
                              return (
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  gap={1}
                                >
                                  <img
                                    src={picture.url}
                                    alt={picture.name}
                                    style={{ width: 26, maxHeight: 30 }}
                                  />
                                  <Typography>{picture.name}</Typography>
                                </Stack>
                              );
                            }
                          };
                        })
                    ]}
                    onChange={(event) => {
                      handleChange(event);
                      setFieldValue('headerImage', event.target.value);
                    }}
                    onBlur={handleBlur}
                    error={touched.headerImage && Boolean(errors.headerImage)}
                  />
                </Grid>
              </Grid>
            </AcCard>
          </Stack>
        )}
      </Grid>
      <Grid item xs={7} className="iphone-mock-wrapper">
        <div className="store-preview-wrapper" style={{ overflow: 'visible' }}>
          <ReceiptPreview
            receiptLogo={defaultValues.logo}
            receiptHeader={defaultValues.headerImage}
          />
        </div>
      </Grid>
    </Grid>
  );
};

export default ReceiptTheme;
